import { useState } from "react";
import { QuickEntry } from "@/components/admin/QuickEntry";

export function ResultsTab({
  allCats, resCategoryId, setResCategoryId, resResult, setResResult,
  resDate, setResDate, resTime, setResTime, resLoading, resReport, resJob, resHistory,
  isGrRes, digitHint, onSubmit, onUndo, onEdit, onChanged,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editDate, setEditDate] = useState("");
  const todayStr = new Date().toLocaleDateString("en-CA");
  const yesterdayStr = new Date(Date.now() - 86400000).toLocaleDateString("en-CA");
  const showYesterdayHint = resDate === todayStr && !resHistory.some((r) => r.date === yesterdayStr);
  return (
    <div data-testid="admin-results-panel" className="max-w-xl">
      <QuickEntry allCats={allCats} resHistory={resHistory} onChanged={onChanged} />
      <div className="mb-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-white/10" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">அல்லது ஒவ்வொன்றாக</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>
      <p className="mb-6 text-sm leading-relaxed text-[#A1A1A6]">
        இன்றைய result-ஐ ஒரே முறை enter பண்ணுங்க — அந்த draw-க்கான எல்லா charts-ம் (yearly, monthly, weekly,
        pending) automatic-ஆ update ஆகி re-render ஆகும். Pending number counters-ம் தானா நகரும்.
      </p>
      {showYesterdayHint && (
        <p data-testid="result-date-hint" className="mb-4 border border-[#FFD700]/30 bg-[#FFD700]/5 px-4 py-3 text-xs leading-relaxed text-[#FFD700]">
          கவனம்: நேத்து ({yesterdayStr}) எந்த entry-ம் இல்லை. நேத்தைய result-ஐ enter பண்றீங்களானா, முதலில் கீழே
          உள்ள Date-ஐ {yesterdayStr}-க்கு மாற்றுங்கள்.
        </p>
      )}
      <form onSubmit={onSubmit} data-testid="result-entry-form" className="space-y-6">
        <div className="flex flex-wrap gap-3">
          <div>
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">Date</p>
            <input
              data-testid="result-date-input"
              type="date"
              value={resDate}
              onChange={(e) => setResDate(e.target.value)}
              className="border border-white/10 bg-black px-4 py-3 text-sm text-white outline-none focus:border-[#FFD700]/60 [color-scheme:dark]"
            />
          </div>
          <div className="min-w-44 flex-1">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">Draw</p>
            <select
              data-testid="result-category-select"
              value={resCategoryId}
              onChange={(e) => setResCategoryId(e.target.value)}
              className="w-full border border-white/10 bg-black px-4 py-3 text-sm text-white outline-none focus:border-[#FFD700]/60"
            >
              <option value="">Select draw</option>
              {allCats.filter((c) => !c.auto_only).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {isGrRes && (
            <div>
              <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">Time</p>
              <select
                data-testid="result-time-select"
                value={resTime}
                onChange={(e) => setResTime(e.target.value)}
                className="border border-white/10 bg-black px-4 py-3 text-sm text-white outline-none focus:border-[#FFD700]/60"
              >
                {["11AM", "12PM", "5PM", "7PM"].map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
            Result number {digitHint && <span className="text-[#FFD700]">({digitHint})</span>}
          </p>
          <input
            data-testid="result-number-input"
            value={resResult}
            onChange={(e) => setResResult(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder="12345"
            className="w-full border border-white/10 bg-black px-5 py-4 font-mono text-2xl font-semibold tracking-[0.3em] text-[#FFD700] outline-none focus:border-[#FFD700]/60"
          />
        </div>
        <button
          data-testid="result-submit-btn"
          type="submit"
          disabled={resLoading}
          className="rounded-full bg-[#FFD700] px-8 py-3 text-xs font-bold uppercase tracking-[0.15em] text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
        >
          {resLoading
            ? resJob?.total
              ? `Updating… ${resJob.done}/${resJob.total} charts`
              : "Accepting result..."
            : "Update all charts"}
        </button>
      </form>
      {resReport && (
        <div data-testid="result-report" className="mt-8 space-y-2">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
            Update report
          </p>
          {resReport.map((r, i) => (
            <div
              key={i}
              data-testid={`result-report-row-${i}`}
              className="flex items-center justify-between gap-4 border border-white/10 bg-black/40 px-4 py-3"
            >
              <span className="truncate text-xs font-semibold">{r.title}</span>
              <span
                className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] ${
                  r.status === "updated" ? "text-[#00E676]" : "text-[#E63946]"
                }`}
              >
                {r.status}
              </span>
            </div>
          ))}
        </div>
      )}

      <div data-testid="results-history" className="mt-10 space-y-2 border-t border-white/10 pt-8">
        <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#666666]">
          Recent results ({resHistory.length})
        </p>
        {resHistory.length === 0 && (
          <p className="border border-dashed border-white/15 py-10 text-center font-mono text-xs uppercase tracking-[0.25em] text-[#666666]">
            No results entered yet
          </p>
        )}
        {resHistory.map((r) => (
          <div
            key={r.id}
            data-testid={`result-history-row-${r.id}`}
            className="flex items-center gap-4 border border-white/10 bg-black/40 px-4 py-3"
          >
            <span className="font-mono text-lg font-semibold tracking-[0.2em] text-[#FFD700]">{r.result}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold">
                {r.category_name}
                {r.time ? ` · ${r.time}` : ""}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#666666]">
                {r.date} ·{" "}
                {r.status === "processing" ? (
                  <span className="text-[#FFD700]">Updating… {r.done ?? 0}/{r.total ?? "?"} charts</span>
                ) : r.status === "undoing" ? (
                  <span className="text-[#FFD700]">Undo நடக்கிறது…</span>
                ) : r.status === "editing" ? (
                  <span className="text-[#FFD700]">Edit நடக்கிறது…</span>
                ) : r.status === "failed" ? (
                  <span className="text-[#E63946]">FAILED — {r.error || "error"}</span>
                ) : (
                  `${r.updated_count ?? 0} charts updated`
                )}
              </p>
            </div>
            {editingId === r.id ? (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <input
                  data-testid={`result-edit-input-${r.id}`}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-28 border border-[#FFD700]/40 bg-black px-3 py-2 font-mono text-sm tracking-[0.15em] text-[#FFD700] outline-none"
                  autoFocus
                />
                <input
                  data-testid={`result-edit-date-${r.id}`}
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="border border-[#FFD700]/40 bg-black px-2 py-2 font-mono text-xs text-[#FFD700] outline-none [color-scheme:dark]"
                />
                <button
                  data-testid={`result-edit-save-${r.id}`}
                  onClick={() => {
                    if (editValue !== r.result || editDate !== r.date) onEdit(r.id, editValue, editDate);
                    setEditingId(null);
                  }}
                  className="rounded-full bg-[#FFD700] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-black transition-colors duration-300 hover:bg-[#00E676]"
                >
                  Save
                </button>
                <button
                  data-testid={`result-edit-cancel-${r.id}`}
                  onClick={() => setEditingId(null)}
                  className="rounded-full border border-white/15 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#A1A1A6] transition-colors duration-300 hover:text-white"
                >
                  ✕
                </button>
              </div>
            ) : ["processing", "undoing", "editing"].includes(r.status) ? (
              <span
                data-testid={`result-busy-${r.id}`}
                className="shrink-0 rounded-full border border-[#FFD700]/30 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#FFD700]"
              >
                Working…
              </span>
            ) : (
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`result-edit-${r.id}`}
                  onClick={() => {
                    setEditingId(r.id);
                    setEditValue(r.result);
                    setEditDate(r.date);
                  }}
                  className="rounded-full border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#A1A1A6] transition-colors duration-300 hover:border-[#FFD700]/60 hover:text-[#FFD700]"
                >
                  Edit
                </button>
                <button
                  data-testid={`result-undo-${r.id}`}
                  onClick={() => onUndo(r.id)}
                  className="rounded-full border border-white/15 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#A1A1A6] transition-colors duration-300 hover:border-[#E63946]/60 hover:text-[#E63946]"
                >
                  Undo
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
